import React from "react";
import loadable from "@loadable/component";
import { useFlags } from "launchdarkly-react-client-sdk";

import { Sentry } from "@compono/sentry-browser";

import { env } from "~/env";
import { useAssistEntitlement } from "~/hooks/useAssistEntitlement";
import { useAuthorization } from "~/hooks/useAuthorization";
import { useHireJobRequisitionsFlag } from "~/hooks/useHireJobRequisitionsFlag";
import { useHireOfferManagementFlag } from "~/hooks/useHireOfferManagementFlag";
import { useCultureReportFlag } from "~/hooks/useCultureReportFlag";
import { useAssistantSettingsAvailable } from "~/hooks/useAssistantSettingsAvailable";
import { useIsTalentRestricted } from "~/auth-layer";
import { useInterviewGuidesEnabled } from "~/components/InterviewGuides/hooks";
import { useInterviewGuidePermissions } from "~/components/InterviewGuides/permissions";
import { openProductPages } from "./chatAiAppLinks";
import { usePrivateCoachingFlag } from "~/hooks/usePrivateCoachingFlag";

const ChatAiIslandHost = loadable(() => import("./ChatAiIslandHost"));

/**
 * Private coaching switched off in this browser, to see the panel as an
 * organisation without coaching sees it: set localStorage `private-coaching`
 * to `off` and reload.
 *
 * It can only take coaching away, never grant it, so it is safe wherever it
 * is set. An organisation's own switch is not this: it belongs on the server,
 * in `/entitlement`'s answer and the chat backend's own refusal.
 */
export const PRIVATE_COACHING_OVERRIDE_KEY = "private-coaching";
const coachingSwitchedOffHere = () => {
  try {
    return window.localStorage.getItem(PRIVATE_COACHING_OVERRIDE_KEY) === "off";
  } catch {
    // Storage blocked (private mode, a sandboxed frame): no override.
    return false;
  }
};

/**
 * Keeps a failing assistant from taking the application down with it.
 *
 * The mount sits at the top of `Private`, a sibling of `LayoutScreen`, and
 * every error boundary in platform-ui lives further down inside the sub-app
 * routers — so without this there is nothing above it to catch anything. A
 * chunk 404 after a deploy (stale index.html), a CDN blip, an ad-blocker
 * matching the chunk name, or the island throwing during module init would
 * unmount the React 17 root: a blank page on every route, for every user who
 * holds the flag and the permission.
 *
 * A `fallback` prop on the loadable would not help — @loadable/component
 * 5.16.7 rethrows a chunk-load error from render() before the fallback branch
 * is reached.
 *
 * Failing closed means no launcher. The assistant is an addition to the page,
 * so its absence costs the user nothing they came for.
 */
class ChatAiBoundary extends React.Component<
  { children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    Sentry.captureException(error);
  }

  render() {
    return this.state.failed ? null : <>{this.props.children}</>;
  }
}

export const ChatAiMount = () => {
  const { chatAiAssistant, referenceChecks } = useFlags();
  const coachingRolledOut = usePrivateCoachingFlag();
  // Two gates, both required. The flag is Compono's: whether the assistant
  // is rolled out here at all. The entitlement is the chat service's answer
  // for this person in this organisation — the assistant per the
  // organisation's "who can use Compono Assist" roles, and private coaching for
  // the same people unless the organisation switched it off — and the service
  // enforces the same answer on every request, so the panel offers exactly
  // what it will accept.
  const entitlement = useAssistEntitlement(Boolean(chatAiAssistant));
  const { permissions } = useAuthorization();
  const hireJobRequisitionsEnabled = useHireJobRequisitionsFlag();
  // What the side nav decides each page's link with, so a help answer's link
  // to a page opens only for someone the nav would show it to.
  const hireOfferManagement = useHireOfferManagementFlag();
  const cultureReport = useCultureReportFlag();
  const assistantSettings = useAssistantSettingsAvailable();
  const talentRestricted = useIsTalentRestricted();
  const interviewGuides = useInterviewGuidesEnabled();
  const { canViewGuides, canViewBank } = useInterviewGuidePermissions();

  // No endpoint means the chat-ai service was not deployed to this stage — PR
  // apps only get it with the `pr app - deploy chat-ai` label. Showing the
  // launcher there would offer a conversation that could never be answered,
  // so the widget stays away entirely rather than failing on first message.
  if (!env.CHAT_AI_URL) return null;

  // The assistant, coaching, or both. Coaching also needs its own rollout
  // flag; a panel with coaching alone opens straight into it.
  if (!chatAiAssistant || !entitlement) return null;
  const assist = entitlement.assist;
  const coaching =
    coachingRolledOut && entitlement.coaching && !coachingSwitchedOffHere();
  if (!assist && !coaching) return null;

  // The condition App.tsx registers the requisition routes under, so a link
  // to a requisition resolves only where its page would render.
  const requisitions = Boolean(
    permissions.product?.hire &&
    hireJobRequisitionsEnabled &&
    permissions.jobRequisitions?.view,
  );

  return (
    <ChatAiBoundary>
      <ChatAiIslandHost
        assist={assist}
        coaching={coaching}
        openPages={{
          requisitions,
          pages: openProductPages({
            permissions,
            talentRestricted,
            canViewGuides,
            canViewBank,
            flags: {
              hireJobRequisitions: hireJobRequisitionsEnabled,
              hireOfferManagement,
              interviewGuides: Boolean(interviewGuides),
              referenceChecks: Boolean(referenceChecks),
              cultureReport,
              assistantSettings,
            },
          }),
        }}
      />
    </ChatAiBoundary>
  );
};
